import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  TextField,
  Button,
  CircularProgress,
  MenuItem,
  Select,
  InputLabel,
  FormControl,
  IconButton,
  Snackbar,
  Alert,
} from "@mui/material";
import AddPhotoAlternateIcon from "@mui/icons-material/AddPhotoAlternate";
import CloseIcon from "@mui/icons-material/Close";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import { auth, db, uploadImage } from "../firebase";
import {
  doc,
  getDoc,
  addDoc,
  collection,
  updateDoc,
  serverTimestamp,
} from "firebase/firestore";
import Header1 from "./Header1";

const Publish = () => {
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    price: "",
    imageUrl: "",
    category: "",
    state: "",
  });
  const [loading, setLoading] = useState(false);
  const [publishSuccess, setPublishSuccess] = useState(false); // State to control publish success
  const history = useNavigate(); // Hook for navigation

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevFormData) => ({
      ...prevFormData,
      [name]: value,
    }));
  };

  const handlePublishOffer = async () => {
    const user = auth.currentUser;
  
    if (!user) {
      console.error("No user is signed in");
      return;
    }
  
    const { uid } = user;
    const userDocRef = doc(db, "users", uid);
    const userDocSnapshot = await getDoc(userDocRef);
  
    if (userDocSnapshot.exists()) {
      const userData = userDocSnapshot.data();
      const {
        displayName: userName,
        profileImageUrl: imageProfile,
        uid: userId,
      } = userData;
  
      // Generar productId
      const productRef = collection(db, "products");
      const newProductDoc = await addDoc(productRef, {
        uid: userId,
        name: userName,
        image: imageProfile,
        title: formData.title,
        description: formData.description,
        imageUrl: formData.imageUrl,
        price: formData.price,
        category: formData.category,
        state: formData.state,
        createdAt: serverTimestamp(),
      });
  
      // Obtener el productId del documento recién agregado
      const productId = newProductDoc.id;
  
      // Actualizar el documento del producto con el productId
      await updateDoc(newProductDoc, { productId });
  
      setPublishSuccess(true); // Set the success state
    } else {
      console.error("User document does not exist");
    }
  };
  

  const handleImageUpload = async (event) => {
    const file = event.target.files[0];

    if (!file) {
      return;
    }

    setLoading(true);

    try {
      const imageUrl = await uploadImage(file);

      setFormData((prevFormData) => ({
        ...prevFormData,
        imageUrl: imageUrl,
      }));
    } catch (error) {
      console.error("Error uploading image:", error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "#f5f6fa", pb: 6 }}>
      <Header1 />

      <Box sx={{ px: 2, pt: 10, maxWidth: 520, mx: "auto" }}>
        <Typography variant="h6" sx={{ fontWeight: 700, mb: 2.5, color: "#1e293b" }}>
          Nueva publicación
        </Typography>

        {/* Preview imagen */}
        <Box
          onClick={() => document.getElementById("upload-button").click()}
          sx={{
            width: "100%",
            aspectRatio: "16/9",
            borderRadius: 3,
            border: formData.imageUrl ? "none" : "2px dashed #cbd5e1",
            bgcolor: formData.imageUrl ? "transparent" : "#f1f5f9",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            cursor: "pointer",
            overflow: "hidden",
            mb: 2,
            position: "relative",
            "&:hover .overlay": { opacity: 1 },
          }}
        >
          {formData.imageUrl ? (
            <>
              <Box component="img" src={formData.imageUrl} alt="preview" sx={{ width: "100%", height: "100%", objectFit: "cover" }} />
              <Box className="overlay" sx={{ position: "absolute", inset: 0, bgcolor: "rgba(0,0,0,0.35)", opacity: 0, transition: "0.2s", display: "flex", alignItems: "center", justifyContent: "center" }}>
                <Typography sx={{ color: "#fff", fontWeight: 600 }}>Cambiar imagen</Typography>
              </Box>
            </>
          ) : (
            <Box sx={{ textAlign: "center", color: "#94a3b8" }}>
              {loading ? <CircularProgress size={32} /> : <>
                <AddPhotoAlternateIcon sx={{ fontSize: 40, mb: 0.5 }} />
                <Typography variant="body2">Tocá para agregar una foto</Typography>
              </>}
            </Box>
          )}
        </Box>
        <input type="file" accept="image/*" onChange={handleImageUpload} style={{ display: "none" }} id="upload-button" onClick={(e) => (e.target.value = null)} />

        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
          <TextField label="Nombre del producto" name="title" value={formData.title} onChange={handleInputChange} fullWidth size="small" />
          <TextField label="Descripción" name="description" value={formData.description} onChange={handleInputChange} fullWidth size="small" multiline rows={3} />
          <TextField label="Precio ($)" name="price" value={formData.price} onChange={handleInputChange} fullWidth size="small" type="number" />

          <FormControl fullWidth size="small">
            <InputLabel>Categoría</InputLabel>
            <Select name="category" value={formData.category} onChange={handleInputChange} label="Categoría">
              {["Electrónica","Moda","Hogar","Deportes","Juguetes","Vehículos","Ropa","Muebles","Libros","Herramientas"].map((c) => (
                <MenuItem key={c} value={c}>{c}</MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl fullWidth size="small">
            <InputLabel>Estado</InputLabel>
            <Select name="state" value={formData.state} onChange={handleInputChange} label="Estado">
              <MenuItem value="Nuevo">Nuevo</MenuItem>
              <MenuItem value="Usado">Usado</MenuItem>
              <MenuItem value="Reacondicionado">Reacondicionado</MenuItem>
              <MenuItem value="De valor histórico">De valor histórico</MenuItem>
            </Select>
          </FormControl>

          <Button
            variant="contained"
            fullWidth
            onClick={handlePublishOffer}
            disabled={!formData.title || !formData.imageUrl || !formData.price}
            sx={{
              mt: 1,
              py: 1.4,
              borderRadius: 3,
              fontWeight: 700,
              fontSize: 15,
              background: "linear-gradient(135deg, #1976d2, #7c3aed)",
              "&:hover": { background: "linear-gradient(135deg, #1565c0, #6d28d9)" },
            }}
          >
            Publicar
          </Button>
        </Box>
      </Box>

      {/* Snackbar éxito */}
      <Snackbar
        open={publishSuccess}
        autoHideDuration={2500}
        onClose={() => { setPublishSuccess(false); history("/home"); }}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          severity="success"
          icon={<CheckCircleOutlineIcon />}
          sx={{ borderRadius: 2, fontWeight: 600 }}
          action={
            <IconButton size="small" onClick={() => { setPublishSuccess(false); history("/home"); }}>
              <CloseIcon fontSize="inherit" />
            </IconButton>
          }
        >
          ¡Publicado con éxito!
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Publish;
